import { AreaChart, BarChart } from '@mantine/charts';
import { Box, Center, Group, Paper, SimpleGrid, Text } from '@mantine/core';

import { BASE_LABELS } from '../../constants/base';
import { analyticsTypes } from '../../types';
import {
  formatChartDate,
  formatCompactAxis,
  formatMoney,
  formatNumber,
  formatPeriodCaption,
  getNiceAxisMax,
  periodCrossesYear,
  toPeriodDates,
} from '../../utils/base';

import { COLORS } from '@/utils/colors';

type DynamicsSectionProps = {
  report: analyticsTypes.BaseAnalyticsReport;
  isDialogsAvailable: boolean;
};

type ChartPoint = { date: string; value: number };

const toChartData = (points: analyticsTypes.BaseAnalyticsDayPoint[], isWithYear: boolean) =>
  points.map((point) => ({
    date: formatChartDate(point.date, isWithYear),
    value: point.value,
  }));

/** Backend pads every available series with zero-days, so "no data" means all-zero, not empty. */
const isEmptySeries = (data: ChartPoint[]) => data.every((point) => point.value === 0);

export const DynamicsSection = ({ report, isDialogsAvailable }: DynamicsSectionProps) => {
  const isWithYear = periodCrossesYear(...toPeriodDates(report.period));
  const revenueData = toChartData(report.dynamics.baseRevenueByDay, isWithYear);
  const leadsData = toChartData(report.dynamics.leadsToBaseByDay, isWithYear);
  const dialogsData = toChartData(report.dynamics.baseDialogsByDay, isWithYear);
  const currency = report.currency;
  const tickGap = revenueData.length > 60 ? 40 : revenueData.length > 30 ? 28 : 12;
  const revenueScaleMax = Math.max(0, ...revenueData.map((point) => point.value));
  const revenueAxisMax = getNiceAxisMax(revenueScaleMax);
  const formatRevenueAxis = (value: number) => formatCompactAxis(value, revenueAxisMax);

  return (
    <Box>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Text fw={600}>{BASE_LABELS.DYNAMICS}</Text>
        <Text size="sm" c="dimmed">
          {formatPeriodCaption(...toPeriodDates(report.period))}
        </Text>
      </Group>
      <Paper p="md" radius="md" withBorder mb="md" bg={COLORS.cardBackground}>
        <Text size="sm" mb="md">
          {BASE_LABELS.REVENUE_BY_DAY}
        </Text>
        {isEmptySeries(revenueData) ? (
          // Leads-only periods pass isEmptyReport — a zero series would render
          // a money chart with a meaningless 0/1/2/3/4 axis.
          <Center h={280}>
            <Text size="sm" c="dimmed" ta="center" maw={280}>
              {BASE_LABELS.REVENUE_EMPTY}
            </Text>
          </Center>
        ) : (
          <BarChart
            h={280}
            data={revenueData}
            dataKey="date"
            series={[{ name: 'value', color: 'teal.5', label: 'База' }]}
            withLegend={false}
            withTooltip
            gridAxis="xy"
            tickLine="y"
            valueFormatter={(value) => formatMoney(value, currency)}
            yAxisProps={{
              domain: [0, revenueAxisMax],
              tickCount: 5,
              tickFormatter: formatRevenueAxis,
              width: 48,
            }}
            xAxisProps={{ minTickGap: tickGap, interval: 'preserveStartEnd' }}
          />
        )}
      </Paper>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <MiniAreaChart
          title={BASE_LABELS.LEADS_BY_DAY}
          data={leadsData}
          color="violet.5"
          valueFormatter={formatNumber}
        />
        <MiniAreaChart
          title={BASE_LABELS.DIALOGS_BY_DAY}
          data={dialogsData}
          color="teal.5"
          valueFormatter={formatNumber}
          emptyMessage={
            isDialogsAvailable ? BASE_LABELS.DIALOGS_EMPTY : BASE_LABELS.DIALOGS_UNAVAILABLE
          }
        />
      </SimpleGrid>
    </Box>
  );
};

type MiniAreaChartProps = {
  title: string;
  data: ChartPoint[];
  color: string;
  valueFormatter: (value: number) => string;
  emptyMessage?: string;
};

const MiniAreaChart = ({
  title,
  data,
  color,
  valueFormatter,
  emptyMessage,
}: MiniAreaChartProps) => {
  const tickGap = data.length > 60 ? 48 : data.length > 30 ? 32 : 16;
  const isShowEmpty = Boolean(emptyMessage) && isEmptySeries(data);
  const scaleMax = Math.max(0, ...data.map((point) => point.value));
  const axisMax = getNiceAxisMax(scaleMax);
  const formatAxis = (value: number) => formatCompactAxis(value, axisMax);

  return (
    <Paper p="md" radius="md" withBorder bg={COLORS.cardBackground}>
      <Group justify="space-between" mb="md">
        <Text size="sm">{title}</Text>
      </Group>
      {isShowEmpty ? (
        <Center h={180}>
          <Text size="sm" c="dimmed" ta="center" maw={280}>
            {emptyMessage}
          </Text>
        </Center>
      ) : (
        <AreaChart
          h={180}
          data={data}
          dataKey="date"
          series={[{ name: 'value', color, label: title }]}
          curveType="monotone"
          withGradient
          withDots={false}
          withLegend={false}
          fillOpacity={0.25}
          gridAxis="xy"
          tickLine="y"
          valueFormatter={valueFormatter}
          yAxisProps={{
            domain: [0, axisMax],
            tickCount: 5,
            tickFormatter: formatAxis,
            width: 36,
            allowDecimals: false,
          }}
          xAxisProps={{ minTickGap: tickGap, interval: 'preserveStartEnd' }}
        />
      )}
    </Paper>
  );
};
